import { TableOutlined } from '@ant-design/icons';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppInput from '@nxsys/components/AppInput';
import AppRadioGroup from '@nxsys/components/AppRadioGroup';
import AppSelect from '@nxsys/components/AppSelect';
import AppSelectSync from '@nxsys/components/AppSelectSync';
import {
  defaultInvoiceRaisedAfterOptions,
  defaultInvoiceRaisedMethodOptions,
  defaultPayrollFreeMethod,
  defaultRuleInvoicingMethod,
  invoiceMethodOptions,
  invoicePaymentModelOptions,
  paymentModels,
  yesNoOptions,
} from '@nxsys/constants/AppConstants';
import { PayrollFeeMethodEnum } from '@nxsys/constants/AppEnums';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { IRELAND, UNITED_KINGDOM } from '@nxsys/store/constants/keys';
import { Option } from '@nxsys/types/models/Common';
import {
  defaultInvoiceTemplateOptions,
  showCurrencyByCountry,
} from '@nxsys/utils';
import {
  afterDraftFeeMethodRule,
  invoiceDueDateRule,
  payrollFeeMethodOptions,
  tieredFeesRule,
} from '@nxsys/utils/formFieldValidation';
import { Form, Tooltip } from 'antd';
import { FormProps } from 'antd/lib';
import React, { Ref, useImperativeHandle, useState } from 'react';
import { useSelector } from 'react-redux';
import AppCurrencyInput from '@nxsys/components/AppCurrencyInput';
import AppRateInput from '@nxsys/components/AppRateInput';
import TieredFeeTableModal, {
  TieredFee,
} from '../../../invoices/components/TieredFeeTableModal';

export type InvoiceSettingValues = {
  raiseInvoice: boolean;
  raiseInvoicePEO: boolean;
  invoiceRaisedAfter: Option<string> | null;
  invoiceRaisedMethod: Option<string> | null;
  dueDate: string;
  rule: Option<string> | null;
  invoiceForSalary: boolean;
  vatSalaryRate: number;
  vatSalaryRateCode: Option<string> | null;
  invoiceForSalaryDescription: string;
  invoiceForERNI: boolean;
  erNIPercent: number;
  vatERNIRate: number;
  vatERNIRateCode: Option<string> | null;
  invoiceForERNIDescription: string;
  invoiceERPension: boolean;
  vatERPensionRate: number;
  vatERPensionRateCode: Option<string> | null;
  invoiceERPensionDescription: string;
  invoiceAppLevy: boolean;
  vatAppLevyRate: number;
  vatAppLevyRateCode: Option<string> | null;
  invoiceAppLevyDescription: string;
  invoicePayrollFee: boolean;
  vatPayrollFeeRate: number;
  vatPayrollFeeRateCode: Option<string> | null;
  invoicePayrollFeeDescription: string;
  invoicingMethod: PayrollFeeMethodEnum;
  feePerEmployee: string;
  tieredFees: Array<TieredFee>;
  invoiceTemplate: Option<string> | null;
  invoicePaymentModel: Option<string> | null;
  invoiceMethod: Option<string> | null;
  invoiceForEmployerPRSI: boolean;
  vatEmployerPRSIRate: number;
  vatEmployerPRSIRateCode: Option<string> | null;
  invoiceForEmployerPRSIDescription: string;
  invoiceForUSC: boolean;
  vatUSCRate: number;
  vatUSCRateCode: Option<string> | null;
  invoiceForUSCDescription: string;
};

export type InvoiceSettingFormRef = {
  submit: () => void;
};

type InvoiceSettingFormProps = {
  formRef?: Ref<InvoiceSettingFormRef>;
  onSubmit?: (values: InvoiceSettingValues) => void;
  defaultValues?: Partial<InvoiceSettingValues>;
};

const InvoiceSettingForm: React.FC<InvoiceSettingFormProps> = ({
  formRef,
  onSubmit,
  defaultValues,
}) => {
  const { messages } = useTypedIntl();
  const { payrollCountry, isBureau } = useSelector(
    (state: AppState) => state.appStore,
  );
  const isIreland = payrollCountry === IRELAND;
  const isUK = isBureau && payrollCountry === UNITED_KINGDOM;

  const [form] = Form.useForm<InvoiceSettingValues>();
  const watchValues = Form.useWatch([], form);

  useImperativeHandle(formRef, () => ({
    submit() {
      form.submit();
    },
  }));

  const [openTieredFeeTable, setOpenTieredFeeTable] = useState(false);
  const toggleTieredFeeTable = () => setOpenTieredFeeTable(!openTieredFeeTable);

  const onFinish: FormProps['onFinish'] = async () => {
    onSubmit?.(form.getFieldsValue(true));
  };

  const onFinishFailed: FormProps['onFinishFailed'] = (errorInfo) => {
    console.log('Failed:', errorInfo);
  };

  return (
    <AppFormContainer
      form={form}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      layout="vertical"
      labelCol={{ span: 24 }}
      wrapperCol={{ span: 24 }}
      initialValues={defaultValues}
    >
      <div className="grid grid-cols-2 gap-x-8 max-sm:grid-cols-1">
        <Form.Item
          label={messages['invoices.raiseInvoice']}
          name="raiseInvoice"
        >
          <AppRadioGroup options={yesNoOptions} />
        </Form.Item>
        {watchValues?.raiseInvoice && (
          <>
            {/* NX-6119: PEO only adds settings to the ER cost invoice, so it
                needs ER cost on. */}
            <Form.Item
              label={messages['invoices.raiseInvoicePEO']}
              name="raiseInvoicePEO"
            >
              <AppRadioGroup options={yesNoOptions} />
            </Form.Item>
            {(!isBureau || isUK) && (
              <>
                {watchValues?.raiseInvoicePEO && (
                  <>
                    <Form.Item
                      label={messages['invoices.invoiceRaisedAfter']}
                      name="invoiceRaisedAfter"
                      rules={[
                        {
                          required: true,
                          message: messages['form.pleaseSelect'],
                        },
                      ]}
                    >
                      <AppSelect
                        options={defaultInvoiceRaisedAfterOptions}
                        placeholder={messages['form.select']}
                      />
                    </Form.Item>
                    <Form.Item
                      label={messages['invoices.invoiceRaisedMethod']}
                      name="invoiceRaisedMethod"
                      rules={[
                        {
                          required: true,
                          message: messages['form.pleaseSelect'],
                        },
                      ]}
                    >
                      <AppSelect
                        options={defaultInvoiceRaisedMethodOptions}
                        placeholder={messages['form.select']}
                      />
                    </Form.Item>
                  </>
                )}
                <Form.Item
                  label={messages['common.dueDate']}
                  name="dueDate"
                  dependencies={['rule']}
                  rules={[
                    { required: true, message: messages['form.pleaseEnter'] },
                    invoiceDueDateRule(messages),
                  ]}
                >
                  <AppRateInput
                    placeholder={messages['form.enter']}
                  />
                </Form.Item>
                <Form.Item
                  label={messages['common.rule']}
                  name="rule"
                  rules={[
                    {
                      required: true,
                      message: messages['form.pleaseSelect'],
                    },
                  ]}
                >
                  <AppSelect
                    options={defaultRuleInvoicingMethod}
                    placeholder={messages['form.select']}
                  />
                </Form.Item>
                <div className="grid grid-cols-2 gap-4 max-sm:grid-cols-1">
                  <Form.Item
                    label={messages['invoices.invoiceForSalary']}
                    name="invoiceForSalary"
                    rules={[
                      {
                        required: true,
                        message: messages['form.pleaseEnter'],
                      },
                    ]}
                  >
                    <AppRadioGroup options={yesNoOptions} />
                  </Form.Item>
                  {watchValues?.raiseInvoicePEO && (
                    <Form.Item
                      label={messages['common.VATRateForSalary']}
                      name="vatSalaryRateCode"
                    >
                      <AppSelectSync
                        type="VATCode"
                        apiType="searchVATCode"
                        placeholder={messages['assignment.rate.selectVatRate']}
                        style={{ width: '100%' }}
                        onChangeValue={(value: any) => {
                          form.setFieldValue('vatSalaryRateCode', value);
                          form.setFieldValue(
                            'vatSalaryRate',
                            Number.parseFloat(value?.fullData?.VATRate),
                          );
                        }}
                      />
                    </Form.Item>
                  )}
                </div>
                <Form.Item
                  label={messages['invoices.invoiceForSalaryDescription']}
                  name="invoiceForSalaryDescription"
                  rules={[
                    {
                      required: watchValues?.invoiceForSalary,
                      message: messages['form.pleaseEnter'],
                    },
                  ]}
                >
                  <AppInput placeholder={messages['form.enter']} />
                </Form.Item>
                <div className="grid grid-cols-2 gap-4 max-sm:grid-cols-1">
                  <Form.Item
                    label={messages['invoices.invoiceForERNI']}
                    name="invoiceForERNI"
                    rules={[
                      {
                        required: true,
                        message: messages['form.pleaseEnter'],
                      },
                    ]}
                  >
                    <AppRadioGroup options={yesNoOptions} />
                  </Form.Item>
                  {watchValues?.raiseInvoicePEO && (
                    <Form.Item
                      label={messages['common.VATRateForERNI']}
                      name="vatERNIRateCode"
                    >
                      <AppSelectSync
                        type="VATCode"
                        apiType="searchVATCode"
                        placeholder={messages['assignment.rate.selectVatRate']}
                        style={{ width: '100%' }}
                        onChangeValue={(value: any) => {
                          form.setFieldValue('vatERNIRateCode', value);
                          form.setFieldValue(
                            'vatERNIRate',
                            Number.parseFloat(value?.fullData?.VATRate),
                          );
                        }}
                      />
                    </Form.Item>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-4 max-sm:grid-cols-1">
                  <Form.Item
                    label={messages['invoices.erNIPercent']}
                    name="erNIPercent"
                    rules={[
                      {
                        required: watchValues?.invoiceForERNI,
                        message: messages['form.pleaseEnter'],
                      },
                    ]}
                  >
                    <AppRateInput
                      placeholder={messages['form.enter']}
                    />
                  </Form.Item>
                  <Form.Item
                    label={messages['invoices.invoiceForERNIDescription']}
                    name="invoiceForERNIDescription"
                    rules={[
                      {
                        required: watchValues?.invoiceForERNI,
                        message: messages['form.pleaseEnter'],
                      },
                    ]}
                  >
                    <AppInput placeholder={messages['form.enter']} />
                  </Form.Item>
                </div>
                <div className="grid grid-cols-2 gap-4 max-sm:grid-cols-1">
                  <Form.Item
                    label={messages['invoices.invoiceERPension']}
                    name="invoiceERPension"
                    rules={[
                      {
                        required: true,
                        message: messages['form.pleaseEnter'],
                      },
                    ]}
                  >
                    <AppRadioGroup options={yesNoOptions} />
                  </Form.Item>
                  {watchValues?.raiseInvoicePEO && (
                    <Form.Item
                      label={messages['common.VATRateForERPension']}
                      name="vatERPensionRateCode"
                    >
                      <AppSelectSync
                        type="VATCode"
                        apiType="searchVATCode"
                        placeholder={messages['assignment.rate.selectVatRate']}
                        style={{ width: '100%' }}
                        onChangeValue={(value: any) => {
                          form.setFieldValue('vatERPensionRateCode', value);
                          form.setFieldValue(
                            'vatERPensionRate',
                            Number.parseFloat(value?.fullData?.VATRate),
                          );
                        }}
                      />
                    </Form.Item>
                  )}
                </div>
                <Form.Item
                  label={messages['invoices.invoiceERPensionDescription']}
                  name="invoiceERPensionDescription"
                  rules={[
                    {
                      required: watchValues?.invoiceERPension,
                      message: messages['form.pleaseEnter'],
                    },
                  ]}
                >
                  <AppInput placeholder={messages['form.enter']} />
                </Form.Item>
                <div className="grid grid-cols-2 gap-4 max-sm:grid-cols-1">
                  <Form.Item
                    label={messages['invoices.invoiceAppLevy']}
                    name="invoiceAppLevy"
                    rules={[
                      {
                        required: true,
                        message: messages['form.pleaseEnter'],
                      },
                    ]}
                  >
                    <AppRadioGroup options={yesNoOptions} />
                  </Form.Item>
                  {watchValues?.raiseInvoicePEO && (
                    <Form.Item
                      label={messages['common.VATRateForAppLevy']}
                      name="vatAppLevyRateCode"
                    >
                      <AppSelectSync
                        type="VATCode"
                        apiType="searchVATCode"
                        placeholder={messages['assignment.rate.selectVatRate']}
                        style={{ width: '100%' }}
                        onChangeValue={(value: any) => {
                          form.setFieldValue('vatAppLevyRateCode', value);
                          form.setFieldValue(
                            'vatAppLevyRate',
                            Number.parseFloat(value?.fullData?.VATRate),
                          );
                        }}
                      />
                    </Form.Item>
                  )}
                </div>
                <Form.Item
                  label={messages['invoices.invoiceAppLevyDescription']}
                  name="invoiceAppLevyDescription"
                  rules={[
                    {
                      required: watchValues?.invoiceAppLevy,
                      message: messages['form.pleaseEnter'],
                    },
                  ]}
                >
                  <AppInput placeholder={messages['form.enter']} />
                </Form.Item>
                <div className="grid grid-cols-2 gap-4 max-sm:grid-cols-1">
                  <Form.Item
                    label={messages['invoices.invoicePayrollFee']}
                    name="invoicePayrollFee"
                    rules={[
                      {
                        required: true,
                        message: messages['form.pleaseEnter'],
                      },
                    ]}
                  >
                    <AppRadioGroup options={yesNoOptions} />
                  </Form.Item>
                  {watchValues?.raiseInvoicePEO && (
                    <Form.Item
                      label={messages['common.VATRateForPayrollFee']}
                      name="vatPayrollFeeRateCode"
                    >
                      <AppSelectSync
                        type="VATCode"
                        apiType="searchVATCode"
                        placeholder={messages['assignment.rate.selectVatRate']}
                        style={{ width: '100%' }}
                        onChangeValue={(value: any) => {
                          form.setFieldValue('vatPayrollFeeRateCode', value);
                          form.setFieldValue(
                            'vatPayrollFeeRate',
                            Number.parseFloat(value?.fullData?.VATRate),
                          );
                        }}
                      />
                    </Form.Item>
                  )}
                </div>
                <Form.Item
                  label={messages['invoices.invoicePayrollFeeDescription']}
                  name="invoicePayrollFeeDescription"
                  rules={[
                    {
                      required: watchValues?.invoicePayrollFee,
                      message: messages['form.pleaseEnter'],
                    },
                  ]}
                >
                  <AppInput placeholder={messages['form.enter']} />
                </Form.Item>
              </>
            )}
            {isIreland && (
              <>
                <Form.Item
                  label={messages['common.paymentModel']}
                  name="invoicePaymentModel"
                  rules={[
                    {
                      required: true,
                      message: messages['form.pleaseSelect'],
                    },
                  ]}
                >
                  <AppSelect
                    options={invoicePaymentModelOptions}
                    placeholder={messages['form.select']}
                  />
                </Form.Item>
                <Form.Item
                  label={messages['common.invoiceMethod']}
                  name="invoiceMethod"
                  rules={[
                    {
                      required: true,
                      message: messages['form.pleaseSelect'],
                    },
                  ]}
                >
                  <AppSelect
                    options={invoiceMethodOptions}
                    placeholder={messages['form.select']}
                  />
                </Form.Item>
                {watchValues?.raiseInvoicePEO && (
                  <>
                    <Form.Item
                      label={messages['invoices.invoiceRaisedAfter']}
                      name="invoiceRaisedAfter"
                      rules={[
                        {
                          required: true,
                          message: messages['form.pleaseSelect'],
                        },
                      ]}
                    >
                      <AppSelect
                        options={defaultInvoiceRaisedAfterOptions}
                        placeholder={messages['form.select']}
                      />
                    </Form.Item>
                    <Form.Item
                      label={messages['invoices.invoiceRaisedMethod']}
                      name="invoiceRaisedMethod"
                      rules={[
                        {
                          required: true,
                          message: messages['form.pleaseSelect'],
                        },
                      ]}
                    >
                      <AppSelect
                        options={defaultInvoiceRaisedMethodOptions}
                        placeholder={messages['form.select']}
                      />
                    </Form.Item>
                  </>
                )}
                <Form.Item
                  label={messages['common.dueDate']}
                  name="dueDate"
                  dependencies={['rule']}
                  rules={[
                    { required: true, message: messages['form.pleaseEnter'] },
                    invoiceDueDateRule(messages),
                  ]}
                >
                  <AppRateInput
                    placeholder={messages['form.enter']}
                  />
                </Form.Item>
                <Form.Item
                  label={messages['common.rule']}
                  name="rule"
                  rules={[
                    {
                      required: true,
                      message: messages['form.pleaseSelect'],
                    },
                  ]}
                >
                  <AppSelect
                    options={defaultRuleInvoicingMethod}
                    placeholder={messages['form.select']}
                  />
                </Form.Item>
                {watchValues?.invoicePaymentModel?.value !==
                  paymentModels.CIS && (
                  <>
                    <div className="grid grid-cols-2 gap-4 max-sm:grid-cols-1">
                      <Form.Item
                        label={messages['invoices.invoiceForSalary']}
                        name="invoiceForSalary"
                        rules={[
                          {
                            required: true,
                            message: messages['form.pleaseEnter'],
                          },
                        ]}
                      >
                        <AppRadioGroup options={yesNoOptions} />
                      </Form.Item>
                      {watchValues?.raiseInvoicePEO && (
                        <Form.Item
                          label={messages['common.VATRateForSalary']}
                          name="vatSalaryRateCode"
                        >
                          <AppSelectSync
                            type="VATCode"
                            apiType="searchVATCode"
                            placeholder={
                              messages['assignment.rate.selectVatRate']
                            }
                            style={{ width: '100%' }}
                            onChangeValue={(value: any) => {
                              form.setFieldValue('vatSalaryRateCode', value);
                              form.setFieldValue(
                                'vatSalaryRate',
                                Number.parseFloat(value?.fullData?.VATRate),
                              );
                            }}
                          />
                        </Form.Item>
                      )}
                    </div>
                    <Form.Item
                      label={messages['invoices.invoiceForSalaryDescription']}
                      name="invoiceForSalaryDescription"
                      rules={[
                        {
                          required: watchValues?.invoiceForSalary,
                          message: messages['form.pleaseEnter'],
                        },
                      ]}
                    >
                      <AppInput placeholder={messages['form.enter']} />
                    </Form.Item>
                    <div className="grid grid-cols-2 gap-4 max-sm:grid-cols-1">
                      <Form.Item
                        label={messages['invoices.invoiceForEmployerPRSI']}
                        name="invoiceForEmployerPRSI"
                        rules={[
                          {
                            required: true,
                            message: messages['form.pleaseEnter'],
                          },
                        ]}
                      >
                        <AppRadioGroup options={yesNoOptions} />
                      </Form.Item>
                      {watchValues?.raiseInvoicePEO && (
                        <Form.Item
                          label={messages['common.VATRateForEmployerPRSI']}
                          name="vatEmployerPRSIRateCode"
                        >
                          <AppSelectSync
                            type="VATCode"
                            apiType="searchVATCode"
                            placeholder={
                              messages['assignment.rate.selectVatRate']
                            }
                            style={{ width: '100%' }}
                            onChangeValue={(value: any) => {
                              form.setFieldValue(
                                'vatEmployerPRSIRateCode',
                                value,
                              );
                              form.setFieldValue(
                                'vatEmployerPRSIRate',
                                Number.parseFloat(value?.fullData?.VATRate),
                              );
                            }}
                          />
                        </Form.Item>
                      )}
                    </div>
                    <Form.Item
                      label={
                        messages['invoices.invoiceForEmployerPRSIDescription']
                      }
                      name="invoiceForEmployerPRSIDescription"
                      rules={[
                        {
                          required: watchValues?.invoiceForEmployerPRSI,
                          message: messages['form.pleaseEnter'],
                        },
                      ]}
                    >
                      <AppInput placeholder={messages['form.enter']} />
                    </Form.Item>
                    <div className="grid grid-cols-2 gap-4 max-sm:grid-cols-1">
                      <Form.Item
                        label={messages['invoices.invoiceERPension']}
                        name="invoiceERPension"
                        rules={[
                          {
                            required: true,
                            message: messages['form.pleaseEnter'],
                          },
                        ]}
                      >
                        <AppRadioGroup options={yesNoOptions} />
                      </Form.Item>
                      {watchValues?.raiseInvoicePEO && (
                        <Form.Item
                          label="VAT rate for ER Pension %"
                          name="vatERPensionRateCode"
                        >
                          <AppSelectSync
                            type="VATCode"
                            apiType="searchVATCode"
                            placeholder={
                              messages['assignment.rate.selectVatRate']
                            }
                            style={{ width: '100%' }}
                            onChangeValue={(value: any) => {
                              form.setFieldValue('vatERPensionRateCode', value);
                              form.setFieldValue(
                                'vatERPensionRate',
                                Number.parseFloat(value?.fullData?.VATRate),
                              );
                            }}
                          />
                        </Form.Item>
                      )}
                    </div>
                    <Form.Item
                      label={messages['invoices.invoiceERPensionDescription']}
                      name="invoiceERPensionDescription"
                      rules={[
                        {
                          required: watchValues?.invoiceERPension,
                          message: messages['form.pleaseEnter'],
                        },
                      ]}
                    >
                      <AppInput placeholder={messages['form.enter']} />
                    </Form.Item>
                    <div className="grid grid-cols-2 gap-4 max-sm:grid-cols-1">
                      <Form.Item
                        label={messages['invoices.invoiceForUSC']}
                        name="invoiceForUSC"
                        rules={[
                          {
                            required: true,
                            message: messages['form.pleaseEnter'],
                          },
                        ]}
                      >
                        <AppRadioGroup options={yesNoOptions} />
                      </Form.Item>
                      {watchValues?.raiseInvoicePEO && (
                        <Form.Item
                          label={messages['common.VATRateForUSC']}
                          name="vatUSCRateCode"
                        >
                          <AppSelectSync
                            type="VATCode"
                            apiType="searchVATCode"
                            placeholder={
                              messages['assignment.rate.selectVatRate']
                            }
                            style={{ width: '100%' }}
                            onChangeValue={(value: any) => {
                              form.setFieldValue('vatUSCRateCode', value);
                              form.setFieldValue(
                                'vatUSCRate',
                                Number.parseFloat(value?.fullData?.VATRate),
                              );
                            }}
                          />
                        </Form.Item>
                      )}
                    </div>
                    <Form.Item
                      label={messages['invoices.invoiceForUSCDescription']}
                      name="invoiceForUSCDescription"
                      rules={[
                        {
                          required: watchValues?.invoiceForUSC,
                          message: messages['form.pleaseEnter'],
                        },
                      ]}
                    >
                      <AppInput placeholder={messages['form.enter']} />
                    </Form.Item>
                  </>
                )}
                <div className="grid grid-cols-2 gap-4 max-sm:grid-cols-1">
                  <Form.Item
                    label={messages['invoices.invoicePayrollFee']}
                    name="invoicePayrollFee"
                    rules={[
                      {
                        required: true,
                        message: messages['form.pleaseEnter'],
                      },
                    ]}
                  >
                    <AppRadioGroup options={yesNoOptions} />
                  </Form.Item>
                  {watchValues?.raiseInvoicePEO && (
                    <Form.Item
                      label={messages['common.VATRateForPayrollFee']}
                      name="vatPayrollFeeRateCode"
                    >
                      <AppSelectSync
                        type="VATCode"
                        apiType="searchVATCode"
                        placeholder={messages['assignment.rate.selectVatRate']}
                        style={{ width: '100%' }}
                        onChangeValue={(value: any) => {
                          form.setFieldValue('vatPayrollFeeRateCode', value);
                          form.setFieldValue(
                            'vatPayrollFeeRate',
                            Number.parseFloat(value?.fullData?.VATRate),
                          );
                        }}
                      />
                    </Form.Item>
                  )}
                </div>
                <Form.Item
                  label={messages['invoices.invoicePayrollFeeDescription']}
                  name="invoicePayrollFeeDescription"
                  rules={[
                    {
                      required: watchValues?.invoicePayrollFee,
                      message: messages['form.pleaseEnter'],
                    },
                  ]}
                >
                  <AppInput placeholder={messages['form.enter']} />
                </Form.Item>
              </>
            )}
            <div className="flex gap-4">
              <Form.Item
                className="flex-1"
                label={messages['invoices.invoicingMethod']}
                name="invoicingMethod"
                dependencies={['raiseInvoicePEO', 'invoiceRaisedAfter']}
                rules={[
                  {
                    required: true,
                    message: messages['form.pleaseSelect'],
                  },
                  afterDraftFeeMethodRule(
                    messages,
                    'raiseInvoicePEO',
                    'invoiceRaisedAfter',
                  ),
                  tieredFeesRule(messages),
                ]}
              >
                <AppSelect
                  options={payrollFeeMethodOptions(
                    defaultPayrollFreeMethod,
                    watchValues?.raiseInvoicePEO,
                    watchValues?.invoiceRaisedAfter,
                  )}
                  placeholder={messages['form.select']}
                />
              </Form.Item>
              {(watchValues?.invoicingMethod as any)?.value ===
                PayrollFeeMethodEnum.TIERED_FEE && (
                <div className="flex items-center">
                  <Tooltip title={messages['common.tieredFeeTable']}>
                    <TableOutlined
                      className="cursor-pointer"
                      onClick={toggleTieredFeeTable}
                    />
                  </Tooltip>
                </div>
              )}
            </div>
            {(watchValues?.invoicingMethod as any)?.value ===
              PayrollFeeMethodEnum.FIXED_FEE_PER_EMPLOYEE && (
              <Form.Item
                key={'feePerEmployee'}
                label={messages['invoices.feePerEmployee']}
                name={'feePerEmployee'}
                rules={[
                  {
                    required: true,
                    message: messages['common.pleaseEnter'],
                  },
                ]}
              >
                <AppCurrencyInput
                  prefix={showCurrencyByCountry(payrollCountry)}
                  placeholder={messages['common.enter']}
                />
              </Form.Item>
            )}
            <Form.Item name="tieredFees" className="hidden">
              {openTieredFeeTable && (
                <TieredFeeTableModal
                  isOpen={openTieredFeeTable}
                  toggle={toggleTieredFeeTable}
                  defaultValues={{
                    tieredFees: watchValues?.tieredFees,
                  }}
                  onSubmit={(values) => {
                    form.setFieldValue('tieredFees', values);
                    // The fee method rule counts the tiers.
                    form.validateFields(['invoicingMethod']).catch(() => {});
                  }}
                />
              )}
            </Form.Item>
            {watchValues?.raiseInvoicePEO && (
              <Form.Item
                label={messages['common.invoiceTemplate']}
                name="invoiceTemplate"
              >
                <AppSelect
                  options={defaultInvoiceTemplateOptions}
                  placeholder={messages['form.select']}
                  defaultValue={defaultInvoiceTemplateOptions[0]}
                  allowClear={false}
                />
              </Form.Item>
            )}
          </>
        )}
      </div>
    </AppFormContainer>
  );
};

export default InvoiceSettingForm;
